import { useEffect, useState } from "react";
import { expendituresApi } from "../../services/businessApi";
import { equipmentApi } from "../../services/equipmentApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { PageHeader } from "../../components/common/PageHeader";
import { LockedFeature } from "../../components/common/LockedFeature";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { Button } from "../../components/common/Button";
import { DataTable } from "../../components/common/DataTable";
import { Pager } from "../../components/common/Pager";
import { Modal } from "../../components/common/Modal";
import { SelectOrAdd } from "../../components/common/SelectOrAdd";
import { Field } from "../../components/common/Field";
import { useFlow } from "../../hooks/useFlow";
import { FLOWS } from "../../services/flows";


export function ExpendituresPage() {
  const { hasFeature } = useFeatureTheme();
  const enabled = hasFeature("ExpenditureTracking");
  const { notify } = useToast();
  const [expenditures, setExpenditures] = useState(null);
  // Paging (#329). This list returned 50 rows and showed them with
  // nothing to say more existed -- 50 of 800 looked exactly like all 50.
  const [pageOffset, setPageOffset] = useState(0);
  const [total, setTotal] = useState(null);
  const [pageLimit, setPageLimit] = useState(null);
  const [paging, setPaging] = useState(false);
  /* The owner has hidden money from this person (#472). They see the
     entries THEY made and nothing else -- see the note by the banner
     below for why that beats showing nothing. */
  const [moneyHidden, setMoneyHidden] = useState(false);
  /* What this person has put through on this account. Their own figures
     back, not the account's. */
  const [yourSpend, setYourSpend] = useState(null);
  const [open, setOpen] = useState(false);
  /* #379. KEYED TO THE DIALOG, not to the page: somebody who comes
     here to read their spending and never opens the form has not
     abandoned anything. */
  /* More than one person has put money through this account, so saying
     WHICH one is worth a column (#505). One name means the answer is
     always "you". */
  const enteredByMoreThanOne =
    new Set((expenditures || []).map((r) => r.logged_by_label).filter(Boolean)).size > 1;

  const flow = useFlow(FLOWS.EXPENDITURE, open);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), category: "Other", amount: "", description: "", item_type: "", quantity: "" });

  async function load(nextOffset = pageOffset) {
    setPaging(true);
    try {
      const r = await expendituresApi.list({ offset: nextOffset });
      /* Checked BEFORE the rows are rendered. The flag does not change
         WHETHER there are rows any more (V121 gives a money-hidden
         helper their own) -- it changes what the list MEANS, and an
         unlabelled subset would read as the account's whole spending. */
      setMoneyHidden(Boolean(r.money_hidden));
      setYourSpend(r.your_spend ?? null);
      setExpenditures(r.expenditures);
      setTotal(r.total ?? null);
      setPageLimit(r.limit ?? null);
    } finally {
      setPaging(false);
    }
  }

  function goTo(nextOffset) {
    setPageOffset(nextOffset);
    load(nextOffset);
  }
  /* Only ask for data the account can use. A locked page still renders (blurred
       preview), but its requests were refused by the server and logged as 403s in
       the console; `enabled` also re-runs this once the plan has loaded. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (enabled) load(); }, [enabled]);

  /* KIT BOUGHT HERE REACHES THE INVENTORY (#507).
   *
   * Reported 6 Oct: "the 'Add Expenditure' 'New Frame', 'New Box',
   * etc... doesn't give you the option to include a quantity and doesn't
   * actually add entries to the inventory. It just records the expense."
   *
   * Correct, and the capability existed on the OTHER screen running the
   * OTHER way: V089 (#319) made an equipment purchase carry the
   * expenditure that paid for it, deliberately rather than writing two
   * rows, because "a careful user records the purchase in Equipment AND
   * writes it up in Expenditures, and it is counted twice with nothing
   * able to tell".
   *
   * So this form does NOT grow a second write path. Supplying a quantity
   * routes the whole thing through the equipment endpoint, which creates
   * the stock and the linked expenditure as ONE event -- V089's
   * invariant preserved exactly, and no new way for the two to disagree.
   *
   * Leaving the quantity blank keeps the old behaviour: fuel, feed and
   * foundation are ordinary spending with no stock behind them. */
  const buyingKit = Boolean(form.quantity) && Number(form.quantity) > 0;

  async function submit() {
    if (!form.amount) return notify("Amount is required", "warning");

    if (buyingKit && !form.item_type) {
      return notify("Say what you bought, so it can reach the inventory", "warning");
    }

    try {
      if (buyingKit) {
        const quantity = Number(form.quantity);
        await equipmentApi.add({
          item_type: form.item_type,
          quantity,
          purchase_date: form.date,
          /* PER UNIT, because that is what the equipment ledger stores --
             and the amount typed here is the TOTAL, which is what a
             receipt shows. Dividing here rather than asking for a unit
             price keeps this form asking the question the receipt
             answers. */
          cost_per_unit: Number(form.amount) / quantity,
          notes: form.description || undefined,
        });
        notify(`${quantity} × ${form.item_type} added, and the cost is in the books`, "success");
      } else {
        await expendituresApi.create({ date: form.date, category: form.category, amount: Number(form.amount), description: form.description || undefined });
        notify("Expenditure added", "success");
      }
      flow.finished();
      setOpen(false);
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not add expenditure", "danger");
    }
  }

  return (
    <div>
      <PageHeader title="Expenditures">
        {/* INSIDE the gate, like SuppliersPage and RecurringOrdersPage.
            Offering the button to everyone let an account without the
            feature fill in the whole form and collect a 403 on save. A
            form you can complete and cannot submit is worse than no
            button. */}
        {enabled && <Button onClick={() => setOpen(true)}>Add Expenditure</Button>}
      </PageHeader>

      {/* PREVIEWED, NOT HIDDEN, and not included either (#387). Free is
          meant to be a shop window: somebody with two hives should see
          what the tool becomes as they grow, so growing means staying.
          Showing this locked creates the pull; giving it away removed the
          reason to move.

          The preview text names the BENEFIT rather than the feature --
          "know what the season actually cost" rather than "expenditure
          tracking" -- because nobody upgrades to be given more forms. */}
      <LockedFeature
        enabled={enabled}
        featureLabel="Expenditure Tracking"
        previewText="Know what the season actually cost you — feed, foundation, fuel and kit, against the honey it produced."
      >
      {/* ENTRY STAYS, AND SO DOES YOUR OWN HISTORY (#472, then V121).
          
          "Can see money" covers expenditures, but refusing the whole
          screen would stop a helper recording a purchase they made on
          the owner's behalf -- often the job they were invited to do. So
          Add Expenditure above is untouched.
          
          NOT a lock-and-upgrade prompt, for the same reason as the P&L:
          no plan grants this. The owner does, with a tick box, and the
          remedy is a conversation.
          
          YOUR OWN ENTRIES, NOT NOTHING (#472 follow-up, V121).
          
          The first version of this showed a helper with money hidden no
          history at all. Asked 3 Oct whether they should see the ones
          they entered, and the answer is yes: hiding somebody's own
          figures back from them protects nothing -- they typed them --
          and as shipped they wrote into a void, with no way to check a
          purchase recorded or spot a typo.
          
          So the list below is the SAME table either way; the server
          decides which rows it holds. A banner says whose they are,
          because a list that silently showed a subset would be the worse
          failure: the helper would read it as the account's whole
          spending and conclude the owner buys almost nothing. */}
      {moneyHidden && (
        <Card title="You are seeing your own entries" className="section-spacing">
          <p style={{ margin: 0, maxWidth: "60ch" }}>
            The owner of this apiary has not given you access to its financial
            information, so this list shows only the expenditures{" "}
            <strong>you</strong> recorded — not the account&apos;s own spending.
          </p>
          {yourSpend !== null && (
            <p style={{ margin: "var(--space-2) 0 0", fontSize: 18, fontWeight: 700 }}>
              ${Number(yourSpend).toFixed(2)}
              <span style={{ fontWeight: 400, fontSize: "var(--font-size-sm)",
                             color: "var(--color-text-muted)" }}>
                {/* THE PERIOD, SAID (#506). Reported 6 Oct: "the total
                    doesn't say if it's all time total or monthly total,
                    or quarterly or yearly."

                    A bare currency figure is not an answer -- somebody
                    reads it as whatever period they had in mind and acts
                    on it, and on a shared account it is the figure most
                    likely to be quoted to the owner. The query behind it
                    has no date filter, so all-time is the truth. */}
                {" "}— what you have put through on this account, all time
              </span>
            </p>
          )}
          <p style={{ margin: "var(--space-2) 0 0", maxWidth: "60ch",
                      color: "var(--color-text-muted)" }}>
            Everything you record is filed to this account for the owner to see.
            If you need the account&apos;s own figures, ask them to turn on
            &ldquo;can see money&rdquo; for you in their team settings.
          </p>
        </Card>
      )}

      <Card>
        <DataTable
          rows={expenditures}
          loading={!expenditures}
          rowKey="expenditure_id"
          /* TWO DIFFERENT EMPTINESSES, and saying the wrong one is a
             false claim. With money hidden the list is the caller's own
             rows, so "no expenditures yet" would read as "this account
             has spent nothing" -- which is exactly the statement the
             server declines to make by omitting the account total. */
          emptyTitle={moneyHidden ? "You have not recorded any yet" : "No expenditures yet"}
          emptyDescription={moneyHidden
            ? "Anything you record with Add Expenditure appears here, and goes to the account for the owner to see."
            : "What you spend running the operation: feed, foundation, fuel, equipment."}
          columns={[
            { key: "date", header: "Date", render: (r) => new Date(r.date).toLocaleDateString() },
            { key: "category", header: "Category" },
            { key: "amount", header: "Amount", render: (r) => `$${Number(r.amount).toFixed(2)}` },
            { key: "description", header: "Description" },
            /*
             * WHO ENTERED IT, ONLY WHEN THAT IS A QUESTION (#505).
             *
             * V121 has stored logged_by_user_id all along and nothing
             * showed it. On a shared account it is what somebody asks the
             * moment a figure looks wrong.
             *
             * Shown only when the rows name MORE THAN ONE person, which
             * is the honest test: on a solo account it would be a column
             * of your own name repeated down the page, and a column that
             * says nothing is a column that trains people to ignore the
             * table. Derived from the data rather than from a separate
             * "is this account shared" call -- the rows already know.
             */
            ...(enteredByMoreThanOne
              ? [{ key: "logged_by_label", header: "Entered by",
                   render: (r) => r.logged_by_label || "—" }]
              : []),
          ]}
        />
        <Pager
          total={total}
          limit={pageLimit}
          offset={pageOffset}
          onChange={goTo}
          busy={paging}
          unit="expenditures"
        />
      </Card>
      </LockedFeature>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Expenditure" footer={<Button onClick={submit}>Save</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <input id="expenditures-page-date" name="date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="touch-target" />
          <SelectOrAdd
            listKey="expense_category"
            label="Category"
            value={form.category}
            onChange={(v) => setForm((f) => ({ ...f, category: v }))}
          />
          <Field label="Amount">
            {(id) => <input id={id} type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="touch-target" />}
          </Field>
          <Field label="Description">
            {(id) => <input id={id} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="touch-target" />}
          </Field>

          {/*
            * BOUGHT KIT? SAY HOW MUCH, AND IT REACHES THE INVENTORY
            * (#507).
            *
            * Optional, and the default is off: fuel, feed and foundation
            * are ordinary spending with no stock behind them, and most
            * rows on this form are those.
            *
            * Filling these in routes the save through the equipment
            * endpoint, so the stock and the money stay ONE event -- see
            * the comment on submit(). This is not a second way to write
            * an expenditure.
            *
            * SelectOrAdd rather than a text box, over the same
            * `equipment_type` list the Equipment page uses, so the two
            * screens cannot invent different names for one thing -- the
            * drift #107 and #417 exist to clean up.
            */}
          <p style={{ margin: 0, color: "var(--color-text-muted)",
                      fontSize: "var(--font-size-sm)" }}>
            Buying equipment? Add a count and it goes into your inventory as
            well as the books.
          </p>
          <SelectOrAdd
            listKey="equipment_type"
            label="What you bought (optional)"
            value={form.item_type}
            onChange={(v) => setForm((f) => ({ ...f, item_type: v }))}
          />
          <Field label="How many (optional)">
            {(id) => (
              <input
                id={id} type="number" min="1" className="touch-target"
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              />
            )}
          </Field>
          {buyingKit && (
            /* SAID BEFORE SAVING, because the amount above is a TOTAL and
               the equipment ledger stores a unit cost. Somebody entering
               £120 for 10 frames should see the £12 before it is
               recorded, not discover it on the Equipment page. */
            <p style={{ margin: 0, fontSize: "var(--font-size-sm)" }}>
              {form.amount
                ? `That is $${(Number(form.amount) / Number(form.quantity)).toFixed(2)} each.`
                : "Add the total amount and this will show the cost each."}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}
